/**
 * CSV writing — one file per sheet, RFC 4180 quoting, formula-injection-safe
 * by default. Generalised from apps/ledger-web/src/lib/export_csv.ts.
 *
 * Output uses CRLF line endings (the RFC 4180 default and what Excel expects on
 * every platform). A cell that would be interpreted as a formula by a
 * spreadsheet app (leading `=`, `+`, `-`, `@`, tab, or CR) is prefixed with a
 * single-quote guard unless `sanitizeFormulas` is turned off — the latter only
 * for internal round-trips re-parsed by our own code, never a user download.
 */

const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

export interface WriteCsvOptions {
  /** Prefix a formula-prone cell with `'` (OWASP CSV injection). Default true. */
  sanitizeFormulas?: boolean;
  /** Line ending between rows. Default CRLF (`\r\n`). */
  eol?: '\r\n' | '\n';
}

function escapeCell(value: string, sanitizeFormulas: boolean): string {
  let s = value;
  if (sanitizeFormulas && FORMULA_PREFIXES.some((p) => s.startsWith(p))) {
    s = `'${s}`;
  }
  if (
    s.includes('"') ||
    s.includes(',') ||
    s.includes('\n') ||
    s.includes('\r')
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a CSV string from a header row and data rows. */
export function buildCsv(
  headers: string[],
  rows: string[][],
  options: WriteCsvOptions = {},
): string {
  const sanitize = options.sanitizeFormulas ?? true;
  const eol = options.eol ?? '\r\n';
  const headerLine = headers.map((c) => escapeCell(c, sanitize)).join(',');
  const bodyLines = rows.map((row) =>
    row.map((c) => escapeCell(c, sanitize)).join(','),
  );
  return [headerLine, ...bodyLines].join(eol);
}
